/**
 * ProfilePage - Generic profile page for Admin, Front Desk, Practitioner
 * Shows basic user info and logout functionality
 */

import { useNavigate } from 'react-router-dom';
import { LogOut, Mail, Building2, Shield } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuthStore } from '@/lib/stores/authStore';
import { Button } from '@/components/base/Button';
import { UserRoleBadge } from '@/components/shared/DynamicNav';

export function ProfilePage() {
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  return (
    <AppLayout>
      <div className="py-8">
        <div className="mx-auto max-w-2xl">
          <h1 className="mb-8 font-hanken text-xl font-medium leading-overview-title tracking-normal text-gray-700">
            My Profile
          </h1>

          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            {/* User Avatar/Initial */}
            <div className="mb-6 flex items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-pranik-brand text-3xl font-bold text-white">
                {user?.firstName?.charAt(0) || user?.username?.charAt(0) || 'U'}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </h2>
                  <UserRoleBadge />
                </div>
                <p className="text-sm text-gray-500">{user?.username}</p>
              </div>
            </div>

            {/* User Details */}
            <div className="space-y-4 border-t border-gray-100 pt-6">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium text-gray-900">{user?.email || 'Not provided'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Role</p>
                  <p className="font-medium text-gray-900">
                    {user?.roleDisplay || user?.role || 'Unknown'}
                  </p>
                </div>
              </div>

              {user?.projectName && (
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Organization</p>
                    <p className="font-medium text-gray-900">{user.projectName}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Logout Button */}
            <div className="mt-8 border-t border-gray-100 pt-6">
              <Button variant="destructive" onClick={handleLogout} className="w-full">
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
